import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import useConfig from '../hooks/useConfig';
import useLoginSession from '../hooks/useLoginSession';

/**
 * Module-level promise cache: prevents duplicate token exchanges during
 * React StrictMode double-mounting in development. The first mount creates
 * and caches the exchange promise; the remount reuses it.
 */
const pendingExchanges = new Map();

export default function Callback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { config } = useConfig();

  const [tokenData, setTokenData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showTokens, setShowTokens] = useState(false);

  const code = searchParams.get('code');
  const returnedState = searchParams.get('state');
  const userIdentifier = searchParams.get('useridentifier');
  const errorParam = searchParams.get('error');
  const errorDesc = searchParams.get('error_description');

  const sessionData = useMemo(() => {
    if (!tokenData) return null;
    return {
      access_token: tokenData.access_token,
      refresh_token: tokenData.refresh_token,
      ttl: tokenData.expires_in,
      attemptId: returnedState,
      userIdentifier,
    };
  }, [tokenData, returnedState, userIdentifier]);

  const { status: sessionStatus, error: sessionError } = useLoginSession(
    config?.operatorGatewayBaseUrl,
    sessionData,
  );

  useEffect(() => {
    let cancelled = false;

    if (errorParam) {
      setError(`${errorParam}: ${errorDesc || 'Unknown error from authorization server'}`);
      setLoading(false);
      return;
    }

    if (!code) {
      setError('No authorization code received in the callback URL.');
      setLoading(false);
      return;
    }

    // On StrictMode remount sessionStorage is already consumed, but a cached
    // exchange promise exists — skip validation and reuse it.
    if (!pendingExchanges.has(code)) {
      const savedState = sessionStorage.getItem('oauth_state');
      if (!returnedState || returnedState !== savedState) {
        setError('OAuth state mismatch — possible CSRF attack. Please try logging in again.');
        setLoading(false);
        return;
      }

      const codeVerifier = sessionStorage.getItem('oauth_code_verifier');
      if (!codeVerifier) {
        setError('Session data lost (missing PKCE verifier). Please go back and log in again.');
        setLoading(false);
        return;
      }

      sessionStorage.removeItem('oauth_state');
      sessionStorage.removeItem('oauth_code_verifier');

      const promise = axios
        .post('/api/token', { code, codeVerifier })
        .then((res) => ({ data: res.data }))
        .catch((err) => ({
          error:
            err.response?.data?.error ||
            err.message ||
            'Token exchange failed',
        }));

      pendingExchanges.set(code, promise);
    }

    pendingExchanges.get(code).then((result) => {
      if (cancelled) return;
      pendingExchanges.delete(code);
      if (result.error) {
        setError(result.error);
      } else {
        setTokenData(result.data);
      }
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [code, returnedState, errorParam, errorDesc]);

  useEffect(() => {
    if (sessionStatus === 'success') {
      console.log('[login-session] success');
    } else if (sessionStatus === 'error') {
      console.error('[login-session] error:', sessionError);
    }
  }, [sessionStatus, sessionError]);

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Failed to copy to clipboard. Your browser may not support this action.');
    }
  };

  const maskToken = (token) => {
    if (!token || token.length <= 8) return '••••••••';
    return token.slice(0, 4) + '••••••••' + token.slice(-4);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-white text-lg">Exchanging authorization code for token...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="bg-gray-800 shadow-xl rounded-2xl p-8 max-w-lg w-full">
          <h2 className="text-2xl font-bold text-red-400 mb-4">Authentication Failed</h2>
          <div className="bg-red-900 bg-opacity-30 border border-red-700 rounded-lg p-4 mb-6">
            <p className="text-red-300 text-sm break-all">{error}</p>
          </div>
          <button
            onClick={() => {
              window.location.href = `funteron://auth-return?attemptId=${encodeURIComponent(returnedState)}&status=error&errorCode=${encodeURIComponent(error)}`;
            }}
            className="bg-green-600 hover:bg-green-500 text-white font-semibold py-2 px-8 rounded-lg transition duration-200"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 p-4">
      <div className="bg-gray-800 shadow-xl rounded-2xl p-8 max-w-2xl w-full">
        <div className="flex items-center justify-center mb-6">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-white">Authentication Successful</h2>
          </div>
        </div>
        <div className="flex justify-center mt-6">
          <button
            onClick={() => {
              window.location.href = `funteron://auth-return?attemptId=${encodeURIComponent(returnedState)}&status=success`;
            }}
            className="bg-green-600 hover:bg-green-500 text-white font-semibold py-2 px-8 rounded-lg transition duration-200"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}
